////////// TODO: REFACTOR/REWRITE THIS COMPONENT TO SUPPORT PROPER CYLINDRICAL/CARTESIAN COORDINATE
import React, { useEffect, useMemo, useState } from "react";
import {
  CartesianCoordinate,
  Coordinate,
  CylindricalCoordinate,
} from "../../../Model/Coordinate";
import {
  EArmType,
  EWaypointDirection,
  Waypoint,
  Vector,
  EAxisType,
  toCartesianWaypoint,
  toCartesianCoordinate,
  toCylindricalCoordinate,
  toCylindricalWaypoint,
  IsCartesianWaypoint,
  toCartesianVector,
  toCylindricalVector,
} from "../../../Model/Waypoint";
import { getModuleState } from "../../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../../redux/reducers/RootReducer";
import MtiButton from "../../Buttons/MtiButton";
import TextBox from "../../Common/TextBox";
import CalibrationApi, {
  RunWaypointPayload,
} from "../../../api/CalibrationApi";
import "./WaypointEditor.css";
import IndeterminateCheckbox from "../../Common/IndeterminateCheckbox";
import SwitchButton from "../../Buttons/SwitchButton";
import { ImagerState, PsrState } from "../../../Model/ModuleState";
import ButtonAlternateLoading from "../../../Icons/ButtonAlternateLoading";
import { useDispatch } from "react-redux";
import { updateModuleDisabledArrow } from "../../../redux/actions/ModuleStateActions";

const loadingIcon = <ButtonAlternateLoading></ButtonAlternateLoading>;

type RunVectorAnimationState =
  | "NO_ANIMATION"
  | "FORWARD"
  | "REVERSE"
  | "SINGLE";

interface Row {
  coordinate: Coordinate;
  velocity: Coordinate;
  isAbsolute: boolean;
  id: number;
  selected: boolean;
  name: string;
}

type Props = {
  waypoint: Waypoint;
  setWaypoint: (waypoint: Waypoint) => void;
  moduleIdentifier: string;
  armType: EArmType;
  style?: React.CSSProperties;
  applyOffsetWhileTuning: boolean;
  isError: boolean;
  setIsError: (isError: boolean) => void;
};


export default function WaypointEditor({
  waypoint,
  setWaypoint,
  armType,
  moduleIdentifier,
  applyOffsetWhileTuning,
  style = {},
  isError,
  setIsError,
}: Props) {
  const dispatch = useDispatch()
  const [selectedIndexes, setSelectedIndexes] = useState([] as number[]);
  const [inputPosAnimatedRowIndex, setInputPosAnimatedRowIndex] = useState(-1);
  const [runVectorAnimationSingleIndex, setRunVectorAnimationSingleIndex] =
    useState(-1);
  const [runVectorAnimationType, setRunVectorAnimationType] = useState(
    "NO_ANIMATION" as RunVectorAnimationState
  );
  const inputPosAnimationTime = 1.5;
  const [positionNameIndexEnabled, setPositionNameEnabled] = useState(-1);

  console.log("DEBUG", moduleIdentifier);

  const isCartesian = waypoint.axisType === EAxisType.Cartesian;

  const moduleStateById = useTypedSelector((state) =>
    getModuleState(state, moduleIdentifier)
  );
  const moduleState =
    armType === EArmType.ImagingArm
      ? moduleStateById.imager
      : moduleStateById.psr;
  const isArmMoving =
    armType === EArmType.ImagingArm
      ? (moduleState as ImagerState).isImagingArmMoving
      : (moduleState as PsrState).isPsrMoving;
  const zero: Coordinate = {
    x: 0,
    y: 0,
    z: 0,
    e: 0,
    t: 0,
  };

  const cartesianZero: CartesianCoordinate = {
    x: 0,
    y: 0,
    z: 0,
  };

  const cylindricalZero: CylindricalCoordinate = {
    t: 0,
    e: 0,
    z: 0,
  };

  const disableMove = isError || isArmMoving;
  let errorMap: { [index: string]: boolean } = {};

  const onErrorOccured = (name: string) => {
    errorMap[name] = true;
    setIsError(true);
  };

  const onErrorCleared = (name: string) => {
    errorMap[name] = false;
    const isError = Object.values(errorMap).some((v) => v === true);
    console.log("DEBUG", { isError }, { errorMap });
    setIsError(isError);
  };

  if (moduleState == null) {
    throw new Error("Module state is null");
  }

  const onRowSelected = (row: Row) => {
    let newSelectedIndexes = [...selectedIndexes];
    let indexOfValue = selectedIndexes.indexOf(row.id);
    if (indexOfValue !== -1) {
      newSelectedIndexes.splice(indexOfValue, 1);
    } else {
      newSelectedIndexes.push(row.id);
    }

    setSelectedIndexes(newSelectedIndexes);
  };

  const onRowSelectAll = (rows: Row[]) => {
    let newSelectedIndexes = [] as number[];
    if (selectedIndexes.length !== rows.length) {
      for (let index = 0; index < rows.length; index++) {
        newSelectedIndexes.push(index);
      }
    }
    setSelectedIndexes(newSelectedIndexes);
  };

  const onRowUpdated = (row: Row) => {
    const newWaypoint = { ...waypoint };
    newWaypoint.vectors[row.id] = {
      ...newWaypoint.vectors[row.id],
      ...row,
    };
    setWaypoint(newWaypoint);
  };

  const onAddNewRow = () => {
    const coordinate =
      waypoint.vectors.length === 0
        ? {
            ...zero,
          }
        : {
            ...waypoint.vectors[waypoint.vectors.length - 1].coordinate,
          };

    let nameText: string;
    let nameNumberUsed: number;

    if (waypoint.vectors.length === 0) {
      // Create default vector name - Position 1
      nameText = "Position";
      nameNumberUsed = 1;
    } else {
      const positionName =
        waypoint.vectors[waypoint.vectors.length - 1].name ??
        data[data.length - 1].name;
      const splittedPositionName = positionName.split(" ");
      const lastName = splittedPositionName.slice(-1)[0];
      const isLastNameNumber = !isNaN(Number.parseInt(lastName));
      nameNumberUsed = isLastNameNumber ? Number.parseInt(lastName) + 1 : 1;
      nameText = splittedPositionName
        .slice(0, isLastNameNumber ? -1 : splittedPositionName.length)
        .join(" ");
    }

    setWaypoint({
      ...waypoint,
      vectors: [
        ...waypoint.vectors,
        {
          axisType: isCartesian ? EAxisType.Cartesian : EAxisType.Cylindrical,
          coordinate: coordinate,
          velocity: {
            ...waypoint.defaultVelocity,
          },
          isAbsolute: false,
          name: `${nameText} ${nameNumberUsed}`,
        },
      ],
    });
  };

  const onDeleteRow = () => {
    const newWaypoint: Waypoint = {
      ...waypoint,
      vectors: waypoint.vectors.filter(
        (value, index) => !selectedIndexes.includes(index)
      ),
    };
    setWaypoint(newWaypoint);
    setSelectedIndexes([]);
  };

  const onInputPosition = (row: Row) => {
    const newWaypoint: Waypoint = {
      ...waypoint,
      vectors: [...waypoint.vectors],
    };

    let psrCoordinate: Coordinate = {
      ...(moduleState as PsrState).cartesianPosition,
      ...(moduleState as PsrState).cylindricalPosition,
    };

    newWaypoint.vectors[row.id] = {
      ...waypoint.vectors[row.id],
      coordinate:
        armType === EArmType.ImagingArm
          ? (moduleState as ImagerState).imagingArmPosition
          : psrCoordinate,
    };

    setInputPosAnimatedRowIndex(row.id);
    setTimeout(() => {
      setInputPosAnimatedRowIndex(-1);
    }, inputPosAnimationTime * 1000 + 100);
    setWaypoint(newWaypoint);
  };

  const onHandleToggleLimitXaxis = (checked: boolean) => {
    setWaypoint({
      ...waypoint,
      ensureXAxisIsSafe: checked
    })
  }

  const getRowAnimationClassName = (row: Row) => {
    if (inputPosAnimatedRowIndex === row.id) {
      return " input-pos-row-animate";
    } else {
      return "";
    }
  };

  const getRowAnimationTimeStyle = (row: Row) => {
    let style = {} as React.CSSProperties;
    if (inputPosAnimatedRowIndex === row.id) {
      style.animationDuration = inputPosAnimationTime + "s";
    }

    return style;
  };

  useEffect(() => {
    if (!disableMove) {
      setRunVectorAnimationType("NO_ANIMATION");
    }
  }, [disableMove]);

  const onMoveUp = () => {
    const minIndex = Math.min(...selectedIndexes);
    const maxIndex = Math.max(...selectedIndexes);
    selectedIndexes.sort();

    if (minIndex < 1 && maxIndex - minIndex + 1 === selectedIndexes.length) {
      return;
    }

    const newWaypoint = { ...waypoint };
    let newSelectedIndexes = [] as number[];
    for (let index = 0; index < selectedIndexes.length; index++) {
      const selectedIndex = selectedIndexes[index];

      if (selectedIndex < 1 || newSelectedIndexes.includes(selectedIndex - 1)) {
        newSelectedIndexes.push(selectedIndex);
        continue;
      }

      const vector = newWaypoint.vectors.splice(selectedIndex, 1);
      newWaypoint.vectors.splice(selectedIndex - 1, 0, vector[0]);
      newSelectedIndexes.push(selectedIndex - 1);
    }

    setWaypoint(newWaypoint);
    setSelectedIndexes(newSelectedIndexes);
  };

  const onMoveDown = () => {
    const minIndex = Math.min(...selectedIndexes);
    const maxIndex = Math.max(...selectedIndexes);
    selectedIndexes.sort().reverse();

    if (
      minIndex >= waypoint.vectors.length - 1 &&
      maxIndex - minIndex + 1 === selectedIndexes.length
    ) {
      return;
    }

    const newWaypoint = { ...waypoint };
    let newSelectedIndexes = [] as number[];
    for (let index = 0; index < selectedIndexes.length; index++) {
      const selectedIndex = selectedIndexes[index];

      if (
        selectedIndex >= newWaypoint.vectors.length - 1 ||
        newSelectedIndexes.includes(selectedIndex + 1)
      ) {
        newSelectedIndexes.push(selectedIndex);
        continue;
      }

      const vector = newWaypoint.vectors.splice(selectedIndex, 1);
      newWaypoint.vectors.splice(selectedIndex + 1, 0, vector[0]);
      newSelectedIndexes.push(selectedIndex + 1);
    }

    setWaypoint(newWaypoint);
    setSelectedIndexes(newSelectedIndexes);
  };

  const onMoveTo = (row: Row) => {
    setRunVectorAnimationType("SINGLE");
    setRunVectorAnimationSingleIndex(row.id);
    if (isCartesian) {
      CalibrationApi.cartesianArmMoveAbsolute(
        moduleIdentifier,
        armType,
        toCartesianVector(waypoint.vectors[row.id])
      );
    } else {
      CalibrationApi.cylindricalArmMoveAbsolute(
        moduleIdentifier,
        armType,
        toCylindricalVector(waypoint.vectors[row.id])
      );
    }
  };

  const onMoveForward = () => {
    setRunVectorAnimationType("FORWARD");

    let payload: RunWaypointPayload = {
      direction: EWaypointDirection.Forward,
    };

    if (waypoint.axisType == EAxisType.Cartesian) {
      payload.cartesianWaypoint = toCartesianWaypoint(waypoint);
      if (!applyOffsetWhileTuning) {
        payload.cartesianWaypoint.forwardOffset = cartesianZero;
      }
    } else {
      payload.cylindricalWaypoint = toCylindricalWaypoint(waypoint);
      if (!applyOffsetWhileTuning) {
        payload.cylindricalWaypoint.forwardOffset = cylindricalZero;
      }
    }

    CalibrationApi.runWaypoint(moduleIdentifier, payload);
  };

  const onMoveReverse = () => {
    setRunVectorAnimationType("REVERSE");
    let payload: RunWaypointPayload = {
      direction: EWaypointDirection.Reverse,
    };

    if (waypoint.axisType == EAxisType.Cartesian) {
      payload.cartesianWaypoint = toCartesianWaypoint(waypoint);
      if (!applyOffsetWhileTuning) {
        payload.cartesianWaypoint.reverseOffset = cartesianZero;
      }
    } else {
      payload.cylindricalWaypoint = toCylindricalWaypoint(waypoint);
      if (!applyOffsetWhileTuning) {
        payload.cylindricalWaypoint.reverseOffset = cylindricalZero;
      }
    }

    CalibrationApi.runWaypoint(moduleIdentifier, payload);
  };

  console.log("RENDERED", { waypoint });

  const data: Row[] = React.useMemo(
    () =>
      waypoint
        ? waypoint.vectors.map((vector, idx) => ({
            id: idx,
            selected: selectedIndexes.includes(idx),
            isAbsolute: vector.isAbsolute,
            coordinate: vector.coordinate,
            velocity: vector.velocity,
            name: vector.name,
          }))
        : [],
    [waypoint, selectedIndexes]
  );

  const disablePositionCheckbox =
    waypoint.supportAutoCalibration && !waypoint.isOverriden;

  const tableRow = data.map((row, index) => (
    <tr
      className={
        row.selected
          ? "waypoint-table-tr waypoint-table-tr-selected" +
            getRowAnimationClassName(row)
          : "waypoint-table-tr" + getRowAnimationClassName(row)
      }
      key={`waypoint-table-row-${index}`}
      style={getRowAnimationTimeStyle(row)}
    >
      <td>
        <IndeterminateCheckbox
          colorVariant="alternate"
          rounded={true}
          style={{ width: 17.3, height: 17.3, paddingLeft: 6 }}
          checked={row.selected}
          onChange={() => onRowSelected(row)}
        />
      </td>
      <td>
        <TextBox
          name="position-name"
          value={row.name}
          variant="text"
          readOnly={positionNameIndexEnabled !== row.id}
          onDoubleClick={() => {
            setPositionNameEnabled(row.id);
          }}
          onChange={(value) =>
            onRowUpdated({
              ...row,
              name: value as string,
            })
          }
          onBlur={() => setPositionNameEnabled(-1)}
          style={{
            width: "100%",
            textAlign: "left",
            borderWidth: 0,
          }}
          className="position-name"
        />
      </td>
      <td>
        <TextBox
          name={isCartesian ? "X" : "T"}
          isInteger={false}
          value={row.coordinate.x ?? row.coordinate.t ?? 0}
          decimalPoint={2}
          variant="number"
          disabled={disablePositionCheckbox}
          onChange={(value) =>
            onRowUpdated({
              ...row,
              coordinate: isCartesian
                ? {
                    ...row.coordinate,
                    x: value as number,
                  }
                : {
                    ...row.coordinate,
                    t: value as number,
                  },
            })
          }
          style={{ minWidth: 50 }}
          onErrorOccured={onErrorOccured}
          onErrorCleared={onErrorCleared}
          onClick={() => dispatch(updateModuleDisabledArrow(true))}
          onBlur={() => dispatch(updateModuleDisabledArrow(false))}
        />
      </td>
      <td>
        <TextBox
          name={isCartesian ? "Y" : "E"}
          isInteger={false}
          value={row.coordinate.y ?? row.coordinate.e ?? 0}
          decimalPoint={2}
          variant="number"
          disabled={disablePositionCheckbox}
          onChange={(value) =>
            onRowUpdated({
              ...row,
              coordinate: isCartesian
                ? {
                    ...row.coordinate,
                    y: value as number,
                  }
                : {
                    ...row.coordinate,
                    e: value as number,
                  },
            })
          }
          style={{ minWidth: 50 }}
          onErrorOccured={onErrorOccured}
          onErrorCleared={onErrorCleared}
          onClick={() => dispatch(updateModuleDisabledArrow(true))}
          onBlur={() => dispatch(updateModuleDisabledArrow(false))}
        />
      </td>
      <td>
        <TextBox
          name="Z"
          isInteger={false}
          value={row.coordinate.z}
          decimalPoint={2}
          variant="number"
          disabled={disablePositionCheckbox}
          onChange={(value) =>
            onRowUpdated({
              ...row,
              coordinate: {
                ...row.coordinate,
                z: value as number,
              },
            })
          }
          style={{ minWidth: 50 }}
          onErrorOccured={onErrorOccured}
          onErrorCleared={onErrorCleared}
          onClick={() => dispatch(updateModuleDisabledArrow(true))}
          onBlur={() => dispatch(updateModuleDisabledArrow(false))}
        />
      </td>
      <td>
        <SwitchButton
          disabled={waypoint.supportAutoCalibration && !waypoint.isOverriden}
          isOn={row.isAbsolute}
          onToggle={() => {
            if (row.selected) {
              onRowUpdated({
                ...row,
                isAbsolute: !row.isAbsolute,
              });
            }
          }}
          key="Abs"
          withLetter={false}
          style={{
            width: 32,
            margin: "auto",
          }}
        />
      </td>
      <td>
        <TextBox
          name={isCartesian ? "VX" : "VT"}
          isInteger={false}
          value={row.velocity.x ?? row.velocity.t ?? 0}
          decimalPoint={2}
          variant="number"
          onChange={(value) =>
            onRowUpdated({
              ...row,
              velocity: isCartesian
                ? {
                    ...row.velocity,
                    x: value as number,
                  }
                : {
                    ...row.velocity,
                    t: value as number,
                  },
            })
          }
          style={{
            minWidth: 50,
          }}
          onErrorOccured={onErrorOccured}
          onErrorCleared={onErrorCleared}
          onClick={() => dispatch(updateModuleDisabledArrow(true))}
          onBlur={() => dispatch(updateModuleDisabledArrow(false))}
        />
      </td>
      <td>
        <TextBox
          name={isCartesian ? "VY" : "VE"}
          isInteger={false}
          value={row.velocity.y ?? row.velocity.e ?? 0}
          decimalPoint={2}
          variant="number"
          onChange={(value) =>
            onRowUpdated({
              ...row,
              velocity: isCartesian
                ? {
                    ...row.velocity,
                    y: value as number,
                  }
                : {
                    ...row.velocity,
                    e: value as number,
                  },
            })
          }
          style={{
            minWidth: 50,
          }}
          onErrorOccured={onErrorOccured}
          onErrorCleared={onErrorCleared}
          onClick={() => dispatch(updateModuleDisabledArrow(true))}
          onBlur={() => dispatch(updateModuleDisabledArrow(false))}
        />
      </td>
      <td>
        <TextBox
          name="VZ"
          isInteger={false}
          value={row.velocity.z}
          decimalPoint={2}
          variant="number"
          onChange={(value) =>
            onRowUpdated({
              ...row,
              velocity: {
                ...row.velocity,
                z: value as number,
              },
            })
          }
          style={{
            minWidth: 50,
          }}
          onErrorOccured={onErrorOccured}
          onErrorCleared={onErrorCleared}
          onClick={() => dispatch(updateModuleDisabledArrow(true))}
          onBlur={() => dispatch(updateModuleDisabledArrow(false))}
        />
      </td>
      <td>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            columnGap: 16,
            justifyContent: "center",
            height: "100%,",
          }}
        >
          <MtiButton
            colorVariant="alternate"
            variant="secondary"
            size="medium"
            disabled={disableMove}
            onClick={() => onInputPosition(row)}
            style={{ minWidth: 40, height: 32 }}
          >
            <svg
              width="20"
              height="16"
              viewBox="0 0 20 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.04348 0H0V16H7.04348V14.6087H1.3913V1.3913H7.04348V0ZM13 7H20V9H13L13.5 13L8 8L13.5 3L13 7Z"
                fill="#0059CC"
              />
            </svg>
          </MtiButton>
          <MtiButton
            variant="secondary"
            colorVariant="alternate"
            size="medium"
            disabled={disableMove}
            onClick={() => onMoveTo(row)}
            style={{
              minWidth: 40,
              height: 32,
            }}
            className={
              disableMove &&
              runVectorAnimationSingleIndex === row.id &&
              runVectorAnimationType === "SINGLE"
                ? "run-vector-row-animate"
                : ""
            }
          >
            {disableMove &&
              runVectorAnimationSingleIndex === row.id &&
              runVectorAnimationType === "SINGLE" &&
              loadingIcon}
            {(!disableMove ||
              runVectorAnimationSingleIndex !== row.id ||
              runVectorAnimationType !== "SINGLE") && (
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8 15.5C12.1422 15.5 15.5 12.1421 15.5 8C15.5 3.85787 12.1422 0.5 8 0.5C3.85785 0.5 0.5 3.85787 0.5 8C0.5 12.1421 3.85785 15.5 8 15.5ZM11.75 8L5.75 4.25V11.75L11.75 8Z"
                  fill="#0059CC"
                />
              </svg>
            )}
          </MtiButton>
        </div>
      </td>
    </tr>
  ));

  const isLimitXShown = useMemo(() => {
    return waypoint.name?.split('_')[0] === 'RetrievePlateVector' && moduleStateById.psr ? true : false
  },[waypoint, moduleStateById.psr])

  return (
    <div style={{ ...style }} className="waypoint-container">
      <div className="vector-name">
        {waypoint.name +
          (waypoint.supportAutoCalibration && !waypoint.isOverriden
            ? " (Auto Calibrated)"
            : "")}
      </div>
      <div className="buttons">
        <MtiButton
          variant="secondary"
          colorVariant="alternate"
          disabled={disableMove}
          onClick={onMoveForward}
          style={{
            width: 133,
          }}
          className={
            disableMove && runVectorAnimationType === "FORWARD"
              ? "run-vector-row-animate"
              : ""
          }
        >
          {disableMove && runVectorAnimationType === "FORWARD" && loadingIcon}
          {(!disableMove || runVectorAnimationType !== "FORWARD") && (
            <>
              <svg
                width="15"
                height="16"
                viewBox="0 0 15 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.5 15.5C11.6422 15.5 15 12.1421 15 8C15 3.85787 11.6422 0.5 7.5 0.5C3.35785 0.5 0 3.85787 0 8C0 12.1421 3.35785 15.5 7.5 15.5ZM11.25 8L5.25 4.25V11.75L11.25 8Z"
                  fill="#0059CC"
                />
              </svg>
              <span>FORWARD</span>
            </>
          )}
        </MtiButton>
        <MtiButton
          variant="secondary"
          colorVariant="alternate"
          disabled={disableMove}
          onClick={onMoveReverse}
          style={{
            width: 125,
          }}
          className={
            disableMove && runVectorAnimationType === "REVERSE"
              ? "run-vector-row-animate"
              : ""
          }
        >
          {disableMove && runVectorAnimationType === "REVERSE" && loadingIcon}
          {(!disableMove || runVectorAnimationType !== "REVERSE") && (
            <>
              <svg
                width="15"
                height="16"
                viewBox="0 0 15 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.5 15.5C11.6422 15.5 15 12.1421 15 8C15 3.85787 11.6422 0.5 7.5 0.5C3.35785 0.5 0 3.85787 0 8C0 12.1421 3.35785 15.5 7.5 15.5ZM11.25 8L5.25 4.25V11.75L11.25 8Z"
                  fill="#0059CC"
                />
              </svg>
              <span>REVERSE</span>
            </>
          )}
        </MtiButton>
        <MtiButton
          variant="secondary"
          colorVariant="alternate"
          disabled={disableMove || waypoint.predefined}
          onClick={onAddNewRow}
          style={{
            width: 127,
          }}
        >
          ADD NEW ROW
        </MtiButton>
        {isLimitXShown && (
          <div className="toggle-wrapper">
            <div className="toggle-label">Limit X-axis at the first position</div>
            <SwitchButton isOn={waypoint.ensureXAxisIsSafe || false} onToggle={onHandleToggleLimitXaxis} withLetter/>
          </div>
        )}
        {selectedIndexes.length > 0 && (
          <>
            <div
              style={{
                borderLeft: "1px solid #E0E0E0",
                height: 21,
                alignSelf: "center",
              }}
            ></div>
            <MtiButton
              colorVariant="alternate"
              disabled={true}
              style={{
                minWidth: 73,
                letterSpacing: 1,
                color: "#414141",
                textTransform: "none",
                fontSize: 12,
              }}
            >
              {selectedIndexes.length} Selected
            </MtiButton>
            <MtiButton
              variant="secondary"
              colorVariant="alternate"
              disabled={disableMove}
              onClick={onMoveUp}
              style={{
                minWidth: 32,
              }}
            >
              <svg
                width="18"
                height="11"
                viewBox="0 0 18 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.75 8.75L9 0.5L17.25 8.75L15.75 10.25L9 3.5L2.25 10.25L0.75 8.75Z"
                  fill="#0059CC"
                />
              </svg>
            </MtiButton>
            <MtiButton
              variant="secondary"
              colorVariant="alternate"
              disabled={disableMove}
              onClick={onMoveDown}
              style={{
                minWidth: 32,
              }}
            >
              <svg
                width="18"
                height="10"
                viewBox="0 0 18 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.75 1.625L9 9.875L17.25 1.625L15.75 0.125L9 6.875L2.25 0.125L0.75 1.625Z"
                  fill="#0059CC"
                />
              </svg>
            </MtiButton>
            <MtiButton
              variant="secondary"
              colorVariant="alternate"
              disabled={disableMove || waypoint.predefined}
              onClick={onDeleteRow}
              style={{
                minWidth: 32,
              }}
            >
              <svg
                width="16"
                height="18"
                viewBox="0 0 16 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.5 2.25V4.5H15.5V2.25H11V0H5V2.25H0.5ZM6.5 1.5V2.25H9.5V1.5H6.5Z"
                  fill="#0059CC"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M2 6H14V18H2V6ZM4.25 15.75V8.25H5.75V15.75H4.25ZM7.25 8.25V15.75H8.75V8.25H7.25ZM10.25 15.75V8.25H11.75V15.75H10.25Z"
                  fill="#0059CC"
                />
              </svg>
            </MtiButton>
          </>
        )}
      </div>
      <div className="waypoint-table-container">
        <table className="waypoint-table">
          <thead>
            <tr>
              <th>
                <IndeterminateCheckbox
                  colorVariant="alternate"
                  rounded={true}
                  style={{ width: 17.3, height: 17.3, paddingLeft: 6 }}
                  onChange={() => onRowSelectAll(data)}
                  checked={selectedIndexes.length === data.length}
                />
              </th>
              <th>Position Name</th>
              <th>{isCartesian ? "X" : "T"}</th>
              <th>{isCartesian ? "Y" : "E"}</th>
              <th>Z</th>
              <th>Absolute</th>
              <th>{isCartesian ? "VX" : "VT"}</th>
              <th>{isCartesian ? "VY" : "VE"}</th>
              <th>VZ</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>{tableRow}</tbody>
        </table>
      </div>
      <div className="waypoint-footer">
        Double click on the position name to <strong>Rename</strong> it
      </div>
    </div>
  );
}
